import {
    headerSummariesFromSelectorPredicate,
    selectorPredicatesForDisplay
} from 'components/TrustCenter/ProviderDetails/SharingAgreementV2/selectorDisplayUtils';

const ORGANIZATION_TYPE_LABELS = Object.freeze({
    0: 'Unknown identifier',
    1: 'SCAC',
    2: 'TenFour XID',
    3: 'Shipper ID',
    4: 'Visibility provider ID',
    5: 'DOT number',
    6: 'MC number',
    7: 'Fleet management customer ID'
});

function unescapeQuoted(value) {
    return String(value).replace(/\\'/g, "'").replace(/\\\\/g, '\\');
}

function organizationLabel(typeNum) {
    if (Number.isInteger(typeNum) && ORGANIZATION_TYPE_LABELS[typeNum] != null) {
        return ORGANIZATION_TYPE_LABELS[typeNum];
    }
    return `Organization identifier type ${typeNum}`;
}

function selectorRawText(selector) {
    if (selector == null) {
        return '';
    }
    if (typeof selector === 'string') {
        return selector.trim();
    }
    try {
        return JSON.stringify(selector);
    } catch (error) {
        return '';
    }
}

/**
 * Turns one stored selector predicate into a short, non-technical description.
 * Falls back to the existing header summary helpers, then the raw expression.
 */
export function friendlySelectorDescription(value) {
    const text = selectorRawText(value);
    if (!text) {
        return '';
    }

    const orgMatch = text.match(
        /^(Shipper|Carrier)Identifiers\[OrganizationIdentifierType=(\d+)\]\.Value\s*=\s*'((?:\\'|[^'])*)'/i
    );
    if (orgMatch) {
        return `${organizationLabel(Number(orgMatch[2]))} is ${unescapeQuoted(orgMatch[3])}`;
    }

    const referenceMapMatch = text.match(
        /ReferenceNumbers\[\s*'((?:\\'|[^'])*)'\s*\]\s*=\s*'((?:\\'|[^'])*)'/i
    );
    if (referenceMapMatch) {
        return `Reference ${unescapeQuoted(referenceMapMatch[1])} is ${unescapeQuoted(
            referenceMapMatch[2]
        )}`;
    }

    const referenceListMatch = text.match(
        /ReferenceNumbersList\[Key\s*=\s*'((?:\\'|[^'])*)'\]\.Value\s*=\s*'((?:\\'|[^'])*)'/i
    );
    if (referenceListMatch) {
        return `Reference ${unescapeQuoted(referenceListMatch[1])} is ${unescapeQuoted(
            referenceListMatch[2]
        )}`;
    }

    const existsReferenceMatch = text.match(
        /\$exists\s*\(\s*ReferenceNumbersList\[Key\s*=\s*'((?:\\'|[^'])*)'\]/i
    );
    if (existsReferenceMatch) {
        return `Has reference ${unescapeQuoted(existsReferenceMatch[1])}`;
    }

    const customerReferenceMatch = text.match(/\.CustomerReference\s*=\s*'((?:\\'|[^'])*)'/i);
    if (customerReferenceMatch) {
        return `Customer reference is ${unescapeQuoted(customerReferenceMatch[1])}`;
    }

    const fieldMatch = text.match(/\.([A-Za-z_][\w]*)\s*=\s*'((?:\\'|[^'])*)'/);
    if (fieldMatch) {
        return `${fieldMatch[1]} is ${unescapeQuoted(fieldMatch[2])}`;
    }

    const summaries = headerSummariesFromSelectorPredicate(text);
    if (summaries.length > 0) {
        return summaries.join(', ');
    }

    return text;
}

export function friendlyIdentifierDescription(identifier) {
    if (!identifier || typeof identifier !== 'object') {
        return '';
    }
    const keys = Array.isArray(identifier.keys)
        ? identifier.keys.map((key) => String(key).trim()).filter(Boolean)
        : [];
    if (keys.length === 1) {
        return `Uses ${keys[0]}`;
    }
    if (keys.length > 1) {
        return `Uses ${keys.join(', ')}`;
    }
    if (identifier.path) {
        return `Values from ${identifier.path}`;
    }
    return '';
}

/**
 * Visible selector rows for the visibility-provider detail page.
 * @returns {{ description: string, expression: string }[]}
 */
export function describeSelectors(selectors) {
    return selectorPredicatesForDisplay((selectors || []).map(selectorRawText).filter(Boolean)).map(
        (expression) => ({
            description: friendlySelectorDescription(expression),
            expression
        })
    );
}

export function describeIdentifiers(identifiers) {
    return (identifiers || [])
        .map((identifier) => ({
            description: friendlyIdentifierDescription(identifier),
            expression: identifierText(identifier)
        }))
        .filter((row) => row.description || row.expression);
}

function identifierText(identifier) {
    if (!identifier || typeof identifier !== 'object') {
        return '';
    }
    const keys = Array.isArray(identifier.keys) ? identifier.keys.join(', ') : '';
    return [keys, identifier.path].filter(Boolean).join(' · ');
}
